// Wave AW.3-React Wave 3 (2026-05-16) — 외부 바이어 내 입찰 화면
// API: GET /buyer-app/me/bids → { success, data: { bids: [...] } }
// 응답의 auction.myBid.status 로 client-side 필터 (탭: 진행중/낙찰/미낙찰)
//
// 단일 source 채택 (옵션 1) — /me/wins 미사용:
//   - winner+pending_selection 윈도우가 어디에도 안 잡히는 누락 차단
//   - 'cancelled' 는 노출 제외 (관리자 무효 처리)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized, displayApiError } from './buyerApi';
import AuctionBuyerCard from './AuctionBuyerCard';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import EmptyState from './EmptyState';
import { useT } from '../../i18n/I18nProvider';

// labelKey 패턴 — t() 호출 (Wave UX-2.1 Item 1 sweep, 2026-05-23)
var TABS = [
  { key: 'active',   labelKey: 'bidsTabActive' },
  { key: 'winner',   labelKey: 'bidsTabWinner' },
  { key: 'rejected', labelKey: 'bidsTabRejected' },
];

function AuctionBuyerBidsList() {
  var navigate = useNavigate();
  var t = useT();
  var [tab, setTab] = useState('active');
  var [allBids, setAllBids] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');

  var load = useCallback(function() {
    setLoading(true);
    setError('');
    authBuyerFetch(API + '/buyer-app/me/bids', {})
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d) {
        if (d && d.success && d.data) {
          setAllBids(d.data.bids || []);
        } else {
          setError(displayApiError(d, t, 'errListLoad'));
        }
        setLoading(false);
      })
      .catch(function(e) {
        if (e && e.message === 'unauthorized') return;
        setError(t('commonNetwork'));
        setLoading(false);
      });
  }, [navigate, t]);

  useEffect(function() { load(); }, [load]);

  // Wave AW.3-bids-reload (2026-05-24) — 출품자 winner 선택 후 바이어가 새로고침 없이
  // 낙찰 탭에서 결과를 볼 수 있도록 2축 자동 reload 추가:
  //   1) 60s polling
  //   2) visibilitychange — 탭 활성화 시 즉시 reload
  useEffect(function() {
    var timer = setInterval(function() {
      if (document.visibilityState === 'visible') load();
    }, 60000);
    var onVis = function() {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVis);
    return function() {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [load]);

  // client-side 필터 — 한 번의 fetch 로 3개 탭 모두 분류.
  var filtered = allBids.filter(function(a) {
    var s = a && a.myBid && a.myBid.status;
    return s === tab;
  });

  var counts = {
    active:   allBids.filter(function(a){ return a && a.myBid && a.myBid.status === 'active'; }).length,
    winner:   allBids.filter(function(a){ return a && a.myBid && a.myBid.status === 'winner'; }).length,
    rejected: allBids.filter(function(a){ return a && a.myBid && a.myBid.status === 'rejected'; }).length,
  };

  var emptyMessage = (function() {
    if (tab === 'active')   return t('bidsEmptyActive');
    if (tab === 'winner')   return t('bidsEmptyWinner');
    if (tab === 'rejected') return t('bidsEmptyRejected');
    return t('commonNoResults');
  })();

  return (
    <AuctionBuyerLayout activeKey="bids">
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 32px 80px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{t('navMyBids')}</h2>
        </div>

        {/* 탭 */}
        <div style={{
          display: 'flex', gap: 4,
          borderBottom: '1.5px solid #e2e8f0',
          marginBottom: 20,
        }}>
          {TABS.map(function(tabDef) {
            var active = tab === tabDef.key;
            var count = counts[tabDef.key];
            return (
              <button key={tabDef.key} type="button"
                onClick={function(){ setTab(tabDef.key); }}
                style={{
                  padding: '12px 18px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: active ? '2.5px solid #1B3A5C' : '2.5px solid transparent',
                  fontSize: 14, fontWeight: active ? 700 : 600,
                  color: active ? '#1B3A5C' : '#64748b',
                  cursor: 'pointer',                  marginBottom: '-1.5px',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                <span>{t(tabDef.labelKey)}</span>
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  background: active ? '#1B3A5C' : '#e2e8f0',
                  color: active ? 'white' : '#64748b',
                  padding: '2px 8px', borderRadius: 10,
                  minWidth: 18, textAlign: 'center',
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* 본문 */}
        {loading && (
          <div style={{ padding: '60px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            {t('commonLoading')}
          </div>
        )}

        {!loading && error && (
          <div style={{ padding: '60px 0', textAlign: 'center' }}>
            <div style={{ color: '#dc2626', fontSize: 14, marginBottom: 12 }}>{error}</div>
            <button type="button" onClick={load}
              style={{
                padding: '8px 16px',
                background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 8,
                fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer',              }}>
              {t('commonRetry')}
            </button>
          </div>
        )}

        {/* Wave TW-1.f.1 (2026-05-24) — winner 탭 상단 거래 완료 확인 진입 배너 */}
        {!loading && !error && tab === 'winner' && (
          <button type="button" onClick={function(){ navigate('/buyer/transactions'); }}
            style={{
              width: '100%', padding: '12px 16px', marginBottom: 16,
              background: 'linear-gradient(135deg, #1B3A5C 0%, #3b82f6 100%)',
              color: 'white', border: 'none', borderRadius: 10,
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
            <span>{t('txConfirmBidsBanner')}</span>
            <span style={{ fontSize: 16 }}>→</span>
          </button>
        )}

        {!loading && !error && filtered.length === 0 && (
          <EmptyState
            icon={tab === 'winner' ? '🏆' : tab === 'rejected' ? '😢' : '🔨'}
            title={emptyMessage}
          />
        )}

        {!loading && !error && filtered.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 16,
          }}>
            {filtered.map(function(a) {
              // Wave UX-2.2 #5 — winner 탭은 카드 클릭 시 채팅으로 진입.
              // Wave UX-2.4 #23 — winner 탭은 카드 아래 [정산 명세서] · [채팅] 분리 액션 추가.
              if (tab === 'winner') {
                return (
                  <div key={a.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <AuctionBuyerCard auction={a}
                      onClick={function(){ navigate('/buyer/invoice/' + a.id); }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" onClick={function(){ navigate('/buyer/invoice/' + a.id); }}
                        style={{ flex: 1, padding: '8px 0', border: '1px solid #1B3A5C', borderRadius: 8, background: 'white', color: '#1B3A5C', fontSize: 12, fontWeight: 700, cursor: 'pointer', }}>
                        📄 {t('invoiceTitle')}
                      </button>
                      <button type="button" onClick={function(){ navigate('/buyer/chat/' + a.id); }}
                        style={{ flex: 1, padding: '8px 0', border: 'none', borderRadius: 8, background: '#1B3A5C', color: 'white', fontSize: 12, fontWeight: 700, cursor: 'pointer', }}>
                        💬 {t('chatTitle')}
                      </button>
                    </div>
                  </div>
                );
              }
              return (
                <AuctionBuyerCard
                  key={a.id}
                  auction={a}
                  onClick={function(){ navigate('/buyer/auctions/' + a.id); }}
                />
              );
            })}
          </div>
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerBidsList;
